import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { Slot } from 'radix-ui'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Tooltip } from './tooltip'

export const buttonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap select-none',
    'transition-[background-color,border-color,color,box-shadow] duration-100',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-brass text-brass-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.14)] hover:bg-brass-hover',
        secondary:
          'border border-line-strong bg-surface text-ink shadow-xs hover:border-ink-3 hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-3 hover:text-ink',
        danger: 'bg-danger text-white hover:bg-danger-hover dark:text-[#1b0907]',
        'danger-ghost': 'text-danger hover:bg-danger-soft',
        link: 'h-auto px-0 text-brass underline-offset-3 hover:underline',
      },
      size: {
        xs: 'h-6 px-2 text-xs [&_svg]:size-3.5',
        sm: 'h-7 px-2.5 text-sm [&_svg]:size-3.5',
        md: 'h-8 px-3 text-sm [&_svg]:size-4',
        lg: 'h-9 px-4 text-base [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element (for example a router <Link>) with button styles. */
  asChild?: boolean
  /** Shows a spinner and disables the button. */
  loading?: boolean
  /** Icon element shown before the label. */
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, icon, children, disabled, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin" aria-hidden /> : icon}
          {children}
        </>
      )}
    </Comp>
  )
})

const iconSizes = { xs: 'size-6', sm: 'size-7', md: 'size-8', lg: 'size-9' } as const

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children'> {
  /** Accessible name; also shown as the tooltip. */
  label: string
  children: ReactNode
  /** Set false to skip the tooltip (for example inside another tooltip trigger). */
  tooltip?: boolean
}

/** A square icon-only button with a required accessible label and tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', variant = 'ghost', className, tooltip = true, ...props },
  ref,
) {
  const btn = (
    <Button
      ref={ref}
      aria-label={label}
      variant={variant}
      size={size}
      className={cn('px-0', iconSizes[size ?? 'md'], className)}
      {...props}
    />
  )
  return tooltip ? <Tooltip content={label}>{btn}</Tooltip> : btn
})
